import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";

const NOTES_CARD_TITLE = "text-sm sm:text-base";

function formatBoardDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "numeric" });
}

function BoardCard({
  board,
  selected,
  editing,
  draftTitle,
  onDraftChange,
  onCommitRename,
  onCancelRename,
  onSelect,
  onDelete,
  canDelete,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const cardClass = cn(
    "group relative flex w-[240px] shrink-0 snap-start snap-always flex-col neo-panel p-3 text-left transition-transform duration-[160ms] ease-in-out sm:w-[260px]",
    selected ? "ring-1 ring-[var(--t-accent)]/35" : "hover:-translate-y-0.5",
  );

  if (editing) {
    return (
      <div className={cardClass} data-testid={`board-card-${board.id}`}>
        <input
          ref={inputRef}
          value={draftTitle}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommitRename(board.id);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              onCancelRename();
            }
          }}
          onBlur={() => onCommitRename(board.id)}
          className={cn(
            "w-full min-w-0 border-0 border-b border-[var(--t-accent)] bg-transparent p-0 font-heading !font-light leading-tight tracking-tight text-[var(--t-text)] outline-none",
            NOTES_CARD_TITLE,
          )}
          data-testid={`board-rename-input-${board.id}`}
          aria-label="Board name"
        />
        <div className="mt-2 flex items-end justify-end border-t border-transparent pt-2">
          <span className="pb-1 text-[10px] font-light leading-none tracking-[0.02em] text-[var(--t-meta)]">
            Updated {formatBoardDate(board.updated_at)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(board.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(board.id);
        }
      }}
      className={cn(cardClass, "cursor-pointer")}
      data-testid={`board-card-${board.id}`}
    >
      {canDelete ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-white/70 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(board);
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--t-border)] bg-white text-[var(--t-meta)] shadow-sm transition-colors hover:border-red-200 hover:text-red-600"
            aria-label={`Delete ${board.title || "board"}`}
            data-testid={`delete-board-${board.id}`}
          >
            <Trash2 size={16} strokeWidth={1.5} />
          </button>
        </div>
      ) : null}
      <h3
        className={cn(
          "min-w-0 truncate font-heading !font-light leading-tight tracking-tight text-[var(--t-text)]",
          NOTES_CARD_TITLE,
        )}
      >
        {board.title || "Untitled board"}
      </h3>
      <div className="mt-2 flex items-end justify-end border-t border-transparent pt-2">
        <span className="pb-1 text-[10px] font-light leading-none tracking-[0.02em] text-[var(--t-meta)]">
          Updated {formatBoardDate(board.updated_at)}
        </span>
      </div>
    </div>
  );
}

export function BoardStrip({
  boards,
  loading,
  activeBoardId,
  onSelect,
  editingBoardId,
  onRenameBoard,
  onEditComplete,
  onDeleteBoard,
  canDelete = false,
}) {
  const [draftTitle, setDraftTitle] = useState("");

  useEffect(() => {
    if (!editingBoardId) return;
    const board = boards.find((b) => b.id === editingBoardId);
    setDraftTitle(board?.title || "");
  }, [editingBoardId, boards]);

  const commitRename = async (boardId) => {
    const trimmed = draftTitle.trim() || "Untitled board";
    await onRenameBoard?.(boardId, trimmed);
    onEditComplete?.();
  };

  if (loading) {
    return <div className="t-meta py-2 text-sm">Loading boards…</div>;
  }

  if (!boards.length) {
    return null;
  }

  return (
    <div
      className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]"
      data-testid="board-strip"
    >
      {boards.map((board) => (
        <BoardCard
          key={board.id}
          board={board}
          selected={board.id === activeBoardId}
          editing={board.id === editingBoardId}
          draftTitle={draftTitle}
          onDraftChange={setDraftTitle}
          onCommitRename={commitRename}
          onCancelRename={onEditComplete}
          onSelect={onSelect}
          onDelete={onDeleteBoard}
          canDelete={canDelete}
        />
      ))}
    </div>
  );
}
